/**
 * OSMDExerciseScene.runScore - how a scale run is laid out on the grand staff.
 *
 * Pure, Phaser-free. The expander decides the notes (pitches, spelling, fingers); this module
 * decides when each sounds, where each sits, and writes the MusicXML the scene renders. The
 * layout reproduces the engraved reference for the major-scale runs (the test fixture
 * `majorScaleEngravingReference.mxl`), and every other scale follows the same rules.
 *
 * Time and bars:
 *   - a run in one direction (one octave up) is one bar with no barlines, every note the same
 *     value, the last included;
 *   - a run up and down is barred every 8 notes from the start, beamed in groups of four, and
 *     ends on its tonic held as a half note. When the run fills its last bar exactly, the half
 *     note takes its own short bar;
 *   - no time signature is ever drawn (the bars are real, the meter is hidden), and the last
 *     barline is the final light-heavy;
 *   - a line holds 4 bars; a closing half-note bar never starts a line on its own.
 *
 * Register: the right hand starts on its tonic written in a window set by the run's range
 * (1 octave: A3 to G#4; 2 octaves: F3 to E4; 3 and 4 octaves: A2 to G#3), and the left hand
 * plays exactly one octave below it.
 *
 * Staves follow the hands, but a hand is not a staff. A hand whose run would need more than 3
 * ledger lines on its own staff moves its part nearest the other staff onto that staff: the
 * left hand from its tonic between A3 and G#4 upward goes to the treble, the right hand from
 * its tonic in that window downward goes to the bass. It moves by whole two-beat spans (a
 * beam group of eighths, a bar of sixteenths): a span moves when more than half of its notes
 * lie at or past that tonic. No octave sign and no clef change is ever written; whatever stays
 * home keeps its ledger lines. In a run where a hand crosses staves, every stem follows its hand:
 * the right hand's up, the left hand's down; elsewhere the renderer chooses.
 */

/** The expander's division space: 4 divisions per quarter note. */
export const RUN_DIVISIONS_PER_QUARTER = 4;

/** The most ledger lines a hand's run may need on its own staff before part of it moves over. */
export const MAX_LEDGER_LINES = 3;

/** Notes per bar in a run up and down. */
export const NOTES_PER_BAR = 8;

/** Notes per beam group. */
export const NOTES_PER_BEAM = 4;

/** Bars per line of music. */
export const BARS_PER_LINE = 4;

/** The held final tonic of a run up and down: a half note. */
export const FINAL_NOTE_DIVISIONS = 2 * RUN_DIVISIONS_PER_QUARTER;

/** A staff change happens between whole spans of this length: two beats. */
export const CROSSING_SPAN_DIVISIONS = 2 * RUN_DIVISIONS_PER_QUARTER;

export type RunHand = 'RH' | 'LH';

export type NoteLetter = 'C' | 'D' | 'E' | 'F' | 'G' | 'A' | 'B';

/** 1 = treble, 2 = bass. */
export type RunStaff = 1 | 2;

const LETTERS: readonly NoteLetter[] = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];

const NATURAL_SEMITONE: Record<NoteLetter, number> = {
  C: 0,
  D: 2,
  E: 4,
  F: 5,
  G: 7,
  A: 9,
  B: 11,
};

const HOME_STAFF: Record<RunHand, RunStaff> = { RH: 1, LH: 2 };

/** The voice each hand is written in, whichever staff a note sits on. */
export const VOICE_OF_HAND: Record<RunHand, number> = { RH: 1, LH: 5 };

/** One written note of the run, in time order within its hand. */
export interface RunScoreNote {
  /** Sounding MIDI pitch. */
  midi: number;
  letter: NoteLetter;
  /** Semitones off the natural letter (-2..2). */
  alteration: number;
  startDivisions: number;
  durationDivisions: number;
  /** The reference finger (1-5), shown above the right hand's notes and below the left's. */
  finger?: number;
}

/** The written (sounding) octave of a spelled pitch: B#3 sounds C4 and is written in octave 3. */
export const writtenOctave = (midi: number, alteration: number): number =>
  Math.floor((midi - alteration) / 12) - 1;

/** Diatonic staff position: 7 per octave, C0 = 0. */
export const staffPosition = (midi: number, letter: NoteLetter, alteration: number): number =>
  writtenOctave(midi, alteration) * 7 + LETTERS.indexOf(letter);

/** A staff's bottom and top lines, as staff positions (treble E4..F5, bass G2..A3). */
const STAFF_LINES: Record<RunStaff, { bottom: number; top: number }> = {
  1: { bottom: 4 * 7 + 2, top: 5 * 7 + 3 },
  2: { bottom: 2 * 7 + 4, top: 3 * 7 + 5 },
};

/** Ledger lines a note at `position` needs on a staff (0 on or next to the staff). */
export const ledgerLinesOn = (position: number, staff: RunStaff): number => {
  const { bottom, top } = STAFF_LINES[staff];
  if (position > top) return Math.floor((position - top) / 2);
  if (position < bottom) return Math.floor((bottom - position) / 2);
  return 0;
};

/** Ledger lines a note at `position` needs on the hand's own staff. */
export const ledgerLines = (position: number, hand: RunHand): number =>
  ledgerLinesOn(position, HOME_STAFF[hand]);

/* ------------------------------------------------------------------ *
 * Register
 * ------------------------------------------------------------------ */

/** A written tonic: its letter and accidental. */
export interface WrittenTonic {
  letter: NoteLetter;
  alteration: number;
}

const position = (letter: NoteLetter, octave: number): number =>
  octave * 7 + LETTERS.indexOf(letter);

/** The lowest staff position of the right hand's starting tonic, by the run's range in octaves. */
const RH_TONIC_WINDOW_BOTTOM: Record<number, number> = {
  1: position('A', 3),
  2: position('F', 3),
  3: position('A', 2),
  4: position('A', 2),
};

/** The window both hands cross staves at: the tonic written between A3 and G#4. */
const CROSSING_TONIC_WINDOW_BOTTOM = position('A', 3);

/** The MIDI pitch of the tonic written within the 7-position window starting at `bottom`. */
const tonicInWindow = (tonic: WrittenTonic, bottom: number): number => {
  const letterIndex = LETTERS.indexOf(tonic.letter);
  const octave = Math.ceil((bottom - letterIndex) / 7);
  return (octave + 1) * 12 + NATURAL_SEMITONE[tonic.letter] + tonic.alteration;
};

export interface RegisterChoice {
  /** The MIDI pitch of each hand's first (lowest) tonic. */
  rhBase: number;
  lhBase: number;
}

/** Each hand's first tonic: the right hand's by the range's window, the left exactly an octave below. */
export const runRegister = (tonic: WrittenTonic, rangeOctaves: number): RegisterChoice => {
  const bottom = RH_TONIC_WINDOW_BOTTOM[rangeOctaves];
  if (bottom === undefined) {
    throw new Error(`scale run: no register for a range of ${rangeOctaves} octaves`);
  }
  const rhBase = tonicInWindow(tonic, bottom);
  return { rhBase, lhBase: rhBase - 12 };
};

/* ------------------------------------------------------------------ *
 * Time: notes, bars, lines, meter
 * ------------------------------------------------------------------ */

export interface RunTiming {
  /** Each note's onset and length, in order (the same for both hands). */
  notes: Array<{ startDivisions: number; durationDivisions: number }>;
  /** The bars as [start, end) in divisions. */
  bars: Array<[number, number]>;
  /** The bars (0-based) that begin a new line, after the first line. */
  lineStarts: number[];
  /** The meter the transport runs the run in: a full bar's (never drawn). */
  meter: [number, number];
}

/** A bar's length as a meter in quarters, eighths or sixteenths, whichever is whole. */
const meterOf = (divisions: number): [number, number] => {
  if (divisions % RUN_DIVISIONS_PER_QUARTER === 0) {
    return [divisions / RUN_DIVISIONS_PER_QUARTER, 4];
  }
  if (divisions % (RUN_DIVISIONS_PER_QUARTER / 2) === 0) {
    return [divisions / (RUN_DIVISIONS_PER_QUARTER / 2), 8];
  }
  return [divisions, 16];
};

/** Lines of `BARS_PER_LINE` bars; a closing bar holding only the held tonic joins the line before. */
const lineStartsOf = (barCount: number, lastBarIsHeldTonicAlone: boolean): number[] => {
  const starts = Array.from(
    { length: Math.ceil(barCount / BARS_PER_LINE) - 1 },
    (_unused, line) => (line + 1) * BARS_PER_LINE,
  );
  const lastStart = starts[starts.length - 1];
  return lastBarIsHeldTonicAlone && lastStart === barCount - 1 ? starts.slice(0, -1) : starts;
};

/**
 * When each of `noteCount` notes sounds, and the bars they fill. `barred` runs (up and down)
 * hold their last note as a half note and are barred every 8 notes; others are one bar.
 */
export const runTiming = (noteCount: number, noteDivisions: number, barred: boolean): RunTiming => {
  if (noteCount < 1) throw new Error('scale run: a run needs at least one note');
  const notes = Array.from({ length: noteCount }, (_unused, index) => ({
    startDivisions: index * noteDivisions,
    durationDivisions: barred && index === noteCount - 1 ? FINAL_NOTE_DIVISIONS : noteDivisions,
  }));
  const last = notes[noteCount - 1];
  const endDivisions = last.startDivisions + last.durationDivisions;
  if (!barred) {
    return { notes, bars: [[0, endDivisions]], lineStarts: [], meter: meterOf(endDivisions) };
  }
  const barDivisions = NOTES_PER_BAR * noteDivisions;
  const fullBars = Math.floor(last.startDivisions / barDivisions);
  const bars: Array<[number, number]> = [
    ...Array.from({ length: fullBars }, (_unused, bar): [number, number] => [
      bar * barDivisions,
      (bar + 1) * barDivisions,
    ]),
    [fullBars * barDivisions, endDivisions],
  ];
  const heldTonicAlone = last.startDivisions === fullBars * barDivisions;
  return {
    notes,
    bars,
    lineStarts: lineStartsOf(bars.length, heldTonicAlone),
    meter: meterOf(barDivisions),
  };
};

/* ------------------------------------------------------------------ *
 * Staves
 * ------------------------------------------------------------------ */

/**
 * The staff each of a hand's notes sits on. A hand that fits its own staff within
 * `MAX_LEDGER_LINES` stays home; otherwise its two-beat spans with more than half their notes
 * at or past the crossing tonic (toward the other staff) move to the other staff.
 */
export const stavesOf = (
  notes: readonly RunScoreNote[],
  hand: RunHand,
  tonic: WrittenTonic,
): RunStaff[] => {
  const home = HOME_STAFF[hand];
  const other: RunStaff = home === 1 ? 2 : 1;
  const fits = notes.every(
    (note) =>
      ledgerLines(staffPosition(note.midi, note.letter, note.alteration), hand) <= MAX_LEDGER_LINES,
  );
  if (fits) return notes.map(() => home);
  const crossingTonic = tonicInWindow(tonic, CROSSING_TONIC_WINDOW_BOTTOM);
  const isPast = (note: RunScoreNote): boolean =>
    hand === 'LH' ? note.midi >= crossingTonic : note.midi <= crossingTonic;
  const spanOf = (note: RunScoreNote): number =>
    Math.floor(note.startDivisions / CROSSING_SPAN_DIVISIONS);
  const pastBySpan = notes.reduce<Map<number, { past: number; total: number }>>((spans, note) => {
    const span = spanOf(note);
    const held = spans.get(span) ?? { past: 0, total: 0 };
    return new Map(spans).set(span, {
      past: held.past + (isPast(note) ? 1 : 0),
      total: held.total + 1,
    });
  }, new Map());
  return notes.map((note) => {
    const { past, total } = pastBySpan.get(spanOf(note))!;
    return past * 2 > total ? other : home;
  });
};

/* ------------------------------------------------------------------ *
 * MusicXML
 * ------------------------------------------------------------------ */

const STEP_TYPES: Record<number, string> = { 8: 'half', 4: 'quarter', 2: 'eighth', 1: '16th' };

/** Beam elements for a note at `indexInGroup` of a beam group of `groupSize` notes. */
const beamsFor = (indexInGroup: number, groupSize: number, durationDivisions: number): string => {
  if (groupSize < 2 || durationDivisions >= RUN_DIVISIONS_PER_QUARTER) return '';
  const value = indexInGroup === 0 ? 'begin' : indexInGroup === groupSize - 1 ? 'end' : 'continue';
  const levels = durationDivisions === 1 ? [1, 2] : [1];
  return levels.map((level) => `<beam number="${level}">${value}</beam>`).join('');
};

export interface RunScoreInput {
  keySignatureFifths: number;
  /** Each hand's notes in time order (a hand that does not play is left out). */
  hands: Partial<Record<RunHand, RunScoreNote[]>>;
  /** The written tonic: where the hands cross staves. */
  tonic: WrittenTonic;
  timing: RunTiming;
  showFingering: boolean;
}

/** One line of the run as its own score: the scene shows each line as a phrase. */
export interface RunLine {
  musicXml: string;
  /** Where the line starts and ends in the run, in divisions. */
  startDivisions: number;
  endDivisions: number;
}

export interface RunScore {
  /** The whole run, its lines broken by `new-system`. */
  musicXml: string;
  /** Each line as a score of its own (clefs and key signature at its start). */
  lines: RunLine[];
  measureCount: number;
  /** Each hand's staves, one per note in its order. */
  staves: Partial<Record<RunHand, RunStaff[]>>;
}

interface PlacedNote {
  note: RunScoreNote;
  hand: RunHand;
  staff: RunStaff;
  /** The run crosses staves: the right hand's stems go up, the left hand's down. */
  stemsByHand: boolean;
}

const noteXml = (
  { note, hand, staff, stemsByHand }: PlacedNote,
  beams: string,
  showFingering: boolean,
): string => {
  const type = STEP_TYPES[note.durationDivisions] ?? 'quarter';
  const alter = note.alteration !== 0 ? `<alter>${note.alteration}</alter>` : '';
  const placement = hand === 'RH' ? 'above' : 'below';
  const fingering =
    showFingering && note.finger !== undefined
      ? `<notations><technical><fingering placement="${placement}">${note.finger}</fingering></technical></notations>`
      : '';
  const stem = stemsByHand ? `<stem>${hand === 'RH' ? 'up' : 'down'}</stem>` : '';
  return (
    `<note><pitch><step>${note.letter}</step>${alter}` +
    `<octave>${writtenOctave(note.midi, note.alteration)}</octave></pitch>` +
    `<duration>${note.durationDivisions}</duration><voice>${VOICE_OF_HAND[hand]}</voice>` +
    `<type>${type}</type>${stem}<staff>${staff}</staff>${beams}${fingering}</note>`
  );
};

/** One hand's notes in one bar, beamed in groups of four counted from the bar's start. */
const handBarXml = (notes: readonly PlacedNote[], showFingering: boolean): string =>
  notes
    .map((placed, index) => {
      const groupStart = index - (index % NOTES_PER_BEAM);
      const group = notes
        .slice(groupStart, groupStart + NOTES_PER_BEAM)
        .filter((other) => other.note.durationDivisions === placed.note.durationDivisions);
      return noteXml(
        placed,
        beamsFor(group.indexOf(placed), group.length, placed.note.durationDivisions),
        showFingering,
      );
    })
    .join('');

const hiddenTimeXml = ([beats, beatType]: [number, number]): string =>
  `<time print-object="no"><beats>${beats}</beats><beat-type>${beatType}</beat-type></time>`;

/** Writes the run's MusicXML: a grand staff, bars and lines by the timing, a final double bar. */
export const writeRunScore = (input: RunScoreInput): RunScore => {
  const handOrder: RunHand[] = ['RH', 'LH'];
  const playing = handOrder.filter((hand) => input.hands[hand] !== undefined);
  const staves = playing.reduce<Partial<Record<RunHand, RunStaff[]>>>(
    (all, hand) => ({ ...all, [hand]: stavesOf(input.hands[hand]!, hand, input.tonic) }),
    {},
  );
  const crosses = playing.some((hand) => staves[hand]!.some((staff) => staff !== HOME_STAFF[hand]));
  const placed = (hand: RunHand): PlacedNote[] =>
    (input.hands[hand] ?? []).map((note, index) => ({
      note,
      hand,
      staff: staves[hand]![index],
      stemsByHand: crosses,
    }));
  const { bars, lineStarts } = input.timing;
  const measureCount = bars.length;

  const opensLine = (index: number): boolean => index === 0 || lineStarts.includes(index);
  /** Bar `index` as measure `number`; `opensScore` gives it the clefs and key, `newSystem` a line break. */
  const measureXml = (
    index: number,
    number: number,
    opensScore: boolean,
    newSystem: boolean,
  ): string => {
    const [start, end] = bars[index];
    const barDivisions = end - start;
    const inBar = (entry: PlacedNote) =>
      entry.note.startDivisions >= start && entry.note.startDivisions < end;
    const isLast = index === measureCount - 1;
    const meterChanges = index > 0 && barDivisions !== bars[index - 1][1] - bars[index - 1][0];
    const attributes = opensScore
      ? `<attributes><divisions>${RUN_DIVISIONS_PER_QUARTER}</divisions>` +
        `<key><fifths>${input.keySignatureFifths}</fifths></key>${hiddenTimeXml(meterOf(barDivisions))}` +
        `<staves>2</staves><clef number="1"><sign>G</sign><line>2</line></clef>` +
        `<clef number="2"><sign>F</sign><line>4</line></clef></attributes>`
      : meterChanges
        ? `<attributes>${hiddenTimeXml(meterOf(barDivisions))}</attributes>`
        : '';
    const newLine = newSystem ? '<print new-system="yes"/>' : '';
    const handsXml = playing
      .map((hand) => handBarXml(placed(hand).filter(inBar), input.showFingering))
      .join(`<backup><duration>${barDivisions}</duration></backup>`);
    const barline = isLast
      ? '<barline location="right"><bar-style>light-heavy</bar-style></barline>'
      : '';
    return `<measure number="${number}">${newLine}${attributes}${handsXml}${barline}</measure>`;
  };
  const scoreXml = (measures: readonly string[]): string =>
    '<?xml version="1.0" encoding="UTF-8" standalone="no"?>' +
    '<score-partwise version="3.1"><part-list><score-part id="P1">' +
    '<part-name print-object="no">Piano</part-name></score-part></part-list>' +
    `<part id="P1">${measures.join('')}</part></score-partwise>`;

  // The whole run: one score, its lines broken by new-system.
  const musicXml = scoreXml(
    bars.map((_bar, index) =>
      measureXml(index, index + 1, index === 0, lineStarts.includes(index)),
    ),
  );
  // Each line on its own: a score that opens with the clefs and the key signature.
  const lineFirstBars = bars.map((_bar, index) => index).filter(opensLine);
  const lines: RunLine[] = lineFirstBars.map((first, line) => {
    const last = (lineFirstBars[line + 1] ?? measureCount) - 1;
    const indexes = Array.from({ length: last - first + 1 }, (_unused, offset) => first + offset);
    return {
      musicXml: scoreXml(
        indexes.map((index, offset) => measureXml(index, offset + 1, offset === 0, false)),
      ),
      startDivisions: bars[first][0],
      endDivisions: bars[last][1],
    };
  });
  return { musicXml, lines, measureCount, staves };
};
